import {
  deleteNotification,
  getActiveNotifications,
  saveNotification,
  updateNotification,
  getUpcomingNotifications,
  getCompletedNotifications,
} from './notification-service';
import { NotificationForm } from './NotificationForm';
import { getAnnouncementTime, getDescription } from './steps/utils';
import { hasPermissionToRender, modalUtils } from '@/app/core/utils';
import { OktaUserInfo } from '@/app/core/auth';
import {
  Notification,
  NotificationDetails,
  NotificationStatus,
  isSuperAdmin,
  AppRoles,
  defaultMaintenanceMsg,
  FeatureCodes,
  UnitOfTimeEnum,
  isAnyProductAdmin,
  NotificationType,
  getProductList,
  ProductNameEnum,
} from '@ssar-app/domain';
import { useEffect, useState } from 'react';
import moment from 'moment';
import {
  Button,
  Flex,
  Grid,
  GridItem,
  HStack,
  Icon,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { Empty, Modal, Pagination, Spin, Tabs } from '@ssar-app/uikit';
import {
  AiOutlineDelete,
  AiOutlineEdit,
  AiOutlineExclamationCircle,
} from 'react-icons/ai/index.js';
import { FaPlusCircle, FaRedo } from 'react-icons/fa/index.js';

export const Notifications = () => {
  const [spinning, setSpinning] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [filterNotification, setFilterNotification] = useState<Notification[]>(
    [],
  );
  const userProfile = OktaUserInfo.getOktaUserInfo();
  const superAdminUser = isSuperAdmin(userProfile);
  const [tabValue, setTabValue] = useState<string>(NotificationStatus.ACTIVE);
  const [notificationType, setNotificationType] = useState<string>(
    superAdminUser
      ? NotificationType.MAINTENANCE
      : NotificationType.ANNOUNCEMENT,
  );
  const [showNotificationForm, setShowNotificationForm] = useState(false);
  const [formMode, setFormMode] = useState<'add' | 'edit'>('add');
  const defaultNotification: Notification = {
    id: '',
    type: superAdminUser
      ? NotificationType.MAINTENANCE
      : NotificationType.ANNOUNCEMENT,
    notify: true,
    title: '',
    description: superAdminUser ? defaultMaintenanceMsg : '',
    details: {
      timeZone: 'America/Chicago',
      unitOfTime: UnitOfTimeEnum.DAYS,
      duration: superAdminUser ? 0 : -1,
    } as NotificationDetails,
    productName: superAdminUser
      ? 'ALL'
      : (getProductList(userProfile)[0] as ProductNameEnum),
  };

  const [notification, setNotification] = useState<Notification>({
    ...defaultNotification,
  });
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const isProductAdmin = isAnyProductAdmin(userProfile);
  const mayCreateNotifications = isProductAdmin
    ? true
    : hasPermissionToRender(
        FeatureCodes.Admin,
        AppRoles.Admin.permissions.MayCreateNotifications,
      );
  const mayEditNotifications = isProductAdmin
    ? true
    : hasPermissionToRender(
        FeatureCodes.Admin,
        AppRoles.Admin.permissions.MayUpdateNotifications,
      );
  const mayDeleteNotifications = isProductAdmin
    ? true
    : hasPermissionToRender(
        FeatureCodes.Admin,
        AppRoles.Admin.permissions.MayDeleteNotifications,
      );

  const loadNotifications = () => {
    setSpinning(true);
    const notificationsCall =
      tabValue === NotificationStatus.ACTIVE
        ? getActiveNotifications
        : tabValue === NotificationStatus.UPCOMING
        ? getUpcomingNotifications
        : getCompletedNotifications;
    notificationsCall()
      .then((response) => {
        if (response.resultCode === 0) {
          setNotifications(response.data || []);
          setFilterNotification(
            (response.data || []).filter(
              (notification) => notification.type === notificationType,
            ),
          );
          setSpinning(false);
        }
      })
      .catch(() => {
        setSpinning(false);
      });
  };
  const handleCreateNotification = () => {
    setFormMode('add');
    setNotification(defaultNotification);
    setShowNotificationForm(true);
  };
  const handleEdit = (notification: Notification) => {
    setFormMode('edit');
    setNotification(notification);
    setShowNotificationForm(true);
  };
  const handleDelete = (notificationId: string) => {
    Modal.confirm({
      header: 'Confirmation',
      icon: <Icon as={AiOutlineExclamationCircle} />,
      headerProps: { display: 'flex', alignItems: 'center', gap: 2 },
      content: `Do you want to delete the Notification?`,
      onOk() {
        setSpinning(true);
        setIsDeleting(true);
        deleteNotification(notificationId)
          .then((response) => {
            if (response.resultCode !== 0) {
              modalUtils.showError('Failed to delete Notification');
            } else {
              modalUtils.showSuccessMessage(
                'Notification deleted successfully.',
              );
              loadNotifications();
            }
          })
          .finally(() => {
            setSpinning(false);
            setIsDeleting(false);
          });
      },
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      onCancel() {},
    });
  };

  useEffect(() => {
    setFilterNotification(
      notifications.filter(
        (notification) => notification.type === notificationType,
      ),
    );
  }, [notificationType, notifications]);

  useEffect(() => {
    setFilterNotification(
      notifications.filter(
        (notification) => notification.type === notificationType,
      ),
    );
  }, [notifications, notificationType]);

  useEffect(() => {
    loadNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue]);

  return (
    <>
      <Grid templateColumns='repeat(2, 1fr)'>
        <GridItem display='flex' alignItems='center'>
          <Text fontWeight={600} fontSize='lg' mb={0} color={'black'}>
            Notification Reports
          </Text>
        </GridItem>
        <GridItem>
          <HStack justifyContent='end'>
            <Button
              colorScheme='gray'
              variant={'outline'}
              onClick={() => loadNotifications()}
            >
              <Icon as={FaRedo} mr={2} fontSize='md' />
              Refresh
            </Button>
            {mayCreateNotifications && (
              <Button
                colorScheme='blue'
                onClick={handleCreateNotification}
                leftIcon={<FaPlusCircle />}
              >
                Create Notification
              </Button>
            )}
          </HStack>
        </GridItem>
      </Grid>
      {showNotificationForm && (
        <NotificationForm
          onCancel={() => setShowNotificationForm(false)}
          getNotifications={loadNotifications}
          data={{ ...notification }}
          saveOrUpdateNotification={
            formMode === 'add' ? saveNotification : updateNotification
          }
        />
      )}
      <Spin
        containerProps={{
          height: 'unset',
          position: 'relative',
          zIndex: 'inherit',
        }}
        spinning={spinning}
        tip={
          isDeleting
            ? 'Deleting Notification. Please wait...'
            : 'Loading Notifications...'
        }
        spinWrapperProps={{ zIndex: 1 }}
        wrapperClassName='center-loader'
        blurContainerProps={spinning ? { opacity: 0.3 } : {}}
      >
        <Tabs
          variant='line'
          onChange={setNotificationType}
          items={Object.values(
            superAdminUser ? NotificationType : [NotificationType.ANNOUNCEMENT],
          ).map((type) => ({
            label: type,
            key: type,
            content: (
              <Tabs
                index={Object.values(NotificationStatus).indexOf(
                  tabValue as NotificationStatus,
                )}
                onChange={setTabValue}
                items={Object.values(NotificationStatus).map((value) => ({
                  label: value,
                  key: value,
                  content: (
                    <>
                      {filterNotification
                        .slice(
                          (currentPage - 1) * pageSize,
                          currentPage * pageSize,
                        )
                        .map((notification, index) => (
                          <Flex
                            flexDirection='column'
                            borderRadius='base'
                            border='1px solid'
                            borderColor='gray.300'
                            p={3}
                            w='full'
                            bg={
                              tabValue === NotificationStatus.ACTIVE
                                ? 'green.100'
                                : tabValue === NotificationStatus.UPCOMING
                                ? 'orange.100'
                                : 'gray.100'
                            }
                            key={index}
                            mt={index !== 0 ? 4 : 0}
                          >
                            <Flex>
                              <Text fontWeight={600} fontSize='md' mb={0}>
                                {notification.title}
                              </Text>
                              <Flex
                                mb={1}
                                gap={2}
                                alignItems='center'
                                ml='auto'
                              >
                                {notification.type ===
                                  NotificationType.ANNOUNCEMENT &&
                                  tabValue === NotificationStatus.UPCOMING && (
                                    <Text fontSize='small' mr={1} mb={0}>
                                      {getAnnouncementTime(notification)}
                                    </Text>
                                  )}
                                <Text fontSize='small' mr={1} mb={0}>
                                  {moment(notification.updatedOn).fromNow()}
                                </Text>
                                {(superAdminUser ||
                                  tabValue !==
                                    NotificationStatus.COMPLETED) && (
                                  <>
                                    {mayEditNotifications && (
                                      <Tooltip hasArrow label='Edit'>
                                        <Flex>
                                          <Icon
                                            as={AiOutlineEdit}
                                            fontSize='md'
                                            cursor='pointer'
                                            onClick={() =>
                                              handleEdit(notification)
                                            }
                                          />
                                        </Flex>
                                      </Tooltip>
                                    )}
                                    {mayDeleteNotifications && (
                                      <Tooltip hasArrow label='Delete'>
                                        <Flex>
                                          <Icon
                                            as={AiOutlineDelete}
                                            fontSize='md'
                                            cursor='pointer'
                                            onClick={() =>
                                              handleDelete(notification.id)
                                            }
                                          />
                                        </Flex>
                                      </Tooltip>
                                    )}
                                  </>
                                )}
                              </Flex>
                            </Flex>
                            {getDescription(notification)}
                          </Flex>
                        ))}
                      {filterNotification.length > 5 && (
                        <Pagination
                          current={currentPage}
                          total={filterNotification.length}
                          defaultPageSize={5}
                          pageSize={pageSize}
                          onChange={(page, pageSize) => {
                            setCurrentPage(page);
                            setPageSize(pageSize);
                          }}
                          pageSizeOptions={[5, 10, 20, 50, 100]}
                          showSizeChanger
                          showTotal={(total, range) =>
                            `${range[0]}-${range[1]} of ${total} items`
                          }
                          containerProps={{ mt: 4, ml: 2 }}
                        />
                      )}
                      {filterNotification.length == 0 && (
                        <Empty description='No Notifications' />
                      )}
                    </>
                  ),
                }))}
                colorScheme='blue'
                variant='unstyled'
                tabProps={{
                  _selected: {
                    border: '0.6px solid blue.800',
                    bg: 'blue.800',
                    color: 'white',
                  },
                  _hover: { color: 'blue.900' },
                  border: '1px solid var(--chakra-colors-gray-300)',
                }}
                tabListProps={{ gap: 0 }}
                tabPanelProps={{ pl: 0, pr: 0 }}
                applyBorderRadiusForStartEndButtons={true}
                borderRadiusForStartEndButtons={5}
                tabLabelProps={{ p: 1 }}
              />
            ),
          }))}
          tabListProps={{ borderColor: 'gray.500' }}
          tabLabelProps={{ fontSize: 'md' }}
          tabProps={{ h: 10 }}
          tabPanelProps={{ pt: 6, pl: 0, pr: 0 }}
        />
      </Spin>
    </>
  );
};
